import { trackEvent } from "@/lib/analytics-events";
import {
  CHAT_LANDING_PATHS,
  INTENT_TAXONOMY_VERSION,
  REPORT_LANDING_PATHS,
  TEXT_INTENT_MATCHERS,
  type CustomerIntent,
} from "@/lib/customer-intent-taxonomy";

type SEOIntentAttribution = {
  seo_intent: string;
  intent_taxonomy_version: string;
  landing_intent: CustomerIntent;
  detected_intent: CustomerIntent;
  intent_confidence: "medium" | "low";
  intent_source: "seo" | "landing" | "unknown";
};

const SEO_INTENT_TO_CUSTOMER_INTENT: Record<string, CustomerIntent> = {
  chart_report: "kundali_report",
  daily_guidance: "low_intent",
  festival_ritual: "timing",
  generic: "unknown",
  remedy_dosha: "remedy_dosha",
  timing_muhurat: "timing",
};

function normalizePath(path?: string): string {
  if (!path) return "";
  try {
    const parsed = path.startsWith("http") ? new URL(path).pathname : path;
    return (
      parsed
        .split("?")[0]
        ?.replace(/\/+/g, "/")
        .replace(/(.+)\/$/, "$1") || "/"
    );
  } catch {
    return (
      path
        .split("?")[0]
        ?.replace(/\/+/g, "/")
        .replace(/(.+)\/$/, "$1") || ""
    );
  }
}

function includesAny(value: string, terms: string[]): boolean {
  return terms.some((term) => value.includes(term));
}

function intentFromText(value?: string): CustomerIntent {
  const text = (value ?? "").toLowerCase();
  const match = TEXT_INTENT_MATCHERS.find(({ terms }) => includesAny(text, terms));
  return match?.intent ?? "unknown";
}

function intentFromPath(path: string): CustomerIntent {
  if (CHAT_LANDING_PATHS.has(path)) return "chat_now";
  if (REPORT_LANDING_PATHS.has(path) || path.includes("kundali")) return "kundali_report";
  return intentFromText(path);
}

function seoIntentAttribution(props: {
  source_path: string;
  intent: string;
}): SEOIntentAttribution {
  const landingIntent = intentFromPath(normalizePath(props.source_path));
  const seoIntent = SEO_INTENT_TO_CUSTOMER_INTENT[props.intent] ?? intentFromText(props.intent);
  const detectedIntent =
    seoIntent !== "unknown" && !(landingIntent === "chat_now" && seoIntent === "low_intent")
      ? seoIntent
      : landingIntent;
  const intentSource =
    detectedIntent === "unknown" ? "unknown" : detectedIntent === seoIntent ? "seo" : "landing";
  return {
    seo_intent: props.intent,
    intent_taxonomy_version: INTENT_TAXONOMY_VERSION,
    landing_intent: landingIntent !== "unknown" ? landingIntent : seoIntent,
    detected_intent: detectedIntent,
    intent_confidence: detectedIntent === "unknown" ? "low" : "medium",
    intent_source: intentSource,
  };
}

export function trackSEOAgentBridgeViewed(props: {
  source_path: string;
  intent: string;
  page_category: string;
  offer_type: string;
  plan_id?: string;
  placement?: string;
  purchase_intent_score?: number;
  purchase_intent_band?: string;
  purchase_intent_reason?: string;
}) {
  trackEvent("seo_agent_bridge_viewed", { ...props, ...seoIntentAttribution(props) });
}

export function trackSEOAgentBridgeClicked(props: {
  source_path: string;
  intent: string;
  page_category: string;
  offer_type: string;
  plan_id?: string;
  target: string;
  placement?: string;
  purchase_intent_score?: number;
  purchase_intent_band?: string;
  purchase_intent_reason?: string;
}) {
  trackEvent("seo_agent_bridge_clicked", { ...props, ...seoIntentAttribution(props) });
}

export function trackSEOIntentCaptured(props: {
  source_path: string;
  intent: string;
  page_category: string;
  target: string;
  placement?: string;
  topic_key?: string;
  chip_prompt?: boolean;
}) {
  trackEvent("seo_intent_captured", { ...props, ...seoIntentAttribution(props) });
}
